import { MoreOutlined, UserOutlined } from "@ant-design/icons"
import {
  Avatar,
  Button,
  Card,
  Dropdown,
  Flex,
  Popover,
  Progress,
  Typography
} from "antd"
import { type ItemType, type MenuItemType } from "antd/es/menu/interface"
import { type BaseOptionType } from "antd/lib/select"
import React, { memo } from "react"
import { useTranslation } from "react-i18next"

import { RecordRef } from "../../../../components/RecordRef.tsx"
import { empty } from "../../../../constants/placeholders"
import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name"
import {
  ProjectStatusSelector,
  type StatusChangeOptions,
  type StatusChangeOutcome
} from "../../../Project/components/ProjectStatusSelector.tsx"
import { ProjectStatusTag } from "../../../Project/components/ProjectStatusTag.tsx"
import { typeLabel } from "../../../Project/helpers/repeated-project-type.ts"
import { creatorLabel } from "../../constants/created.ts"
import { getProjectCustomerName } from "../../constants/helpers.ts"
import { normalizePercent } from "../../constants/helpers.ts"
import { getProgressColor } from "../../constants/project-progress.ts"
import { type ProjectListItem } from "../../types/types.ts"
import { ProjectCardPhoto } from "./ProjectCardPhoto.tsx"

const { Text, Title } = Typography

interface ProjectGridCardProps {
  project: ProjectListItem
  statuses: BaseOptionType[]
  statusEditable: boolean
  canClose: boolean
  photoEditable: boolean
  showProgress: boolean
  showBudget: boolean
  actionItems?: ItemType<MenuItemType>[]
  onClick?: (id: string) => void
  onStatusChange: (
    id: string,
    status: number,
    options?: StatusChangeOptions
  ) => Promise<StatusChangeOutcome>
  onUpdateMainPhoto: (
    projectId: string,
    file?: File | null
  ) => Promise<string | null>
}

export const ProjectGridCard = memo(
  ({
    project,
    statuses,
    statusEditable,
    canClose,
    photoEditable,
    showProgress,
    showBudget,
    actionItems,
    onClick,
    onStatusChange,
    onUpdateMainPhoto
  }: ProjectGridCardProps) => {
    const { t } = useTranslation()
    const progress = project?.progress || "0%"
    const progressValue = normalizePercent(progress)
    const managers = project?.project_managers || []
    const mainManager = managers?.find((m) => m?.is_main) ?? managers?.[0]
    const otherManagers =
      managers?.filter((m) => m?.id !== mainManager?.id) || []
    const hasOtherManagers = otherManagers.length > 0

    const onStatusChangeHandler = (
      status: number,
      options?: StatusChangeOptions
    ) => onStatusChange(project.id, status, options)

    const formatedPrice = budgetFormat(project?.sales_price)
    const customerName = getProjectCustomerName(project) || null
    const { formatDate, dateFormat } = useDateFormatter()
    const createdLine = `Created ${formatDate(project?.created, dateFormat) || empty} · ${creatorLabel(project?.created_by)}`
    const typeDisplay =
      project?.project_type_details?.map(typeLabel).join(", ") || null

    const otherManagersContent = (
      <Flex vertical gap={8} className="max-h-48 overflow-auto">
        {otherManagers.map((manager) => (
          <Flex key={manager.id} gap={8} align="center">
            <Avatar
              src={manager?.avatar}
              size="small"
              icon={<UserOutlined />}
            />
            <Text className="text-xs">{getEntityName(manager)}</Text>
          </Flex>
        ))}
      </Flex>
    )

    return (
      <Card
        hoverable
        classNames={{ body: "p-2" }}
        className="overflow-hidden relative"
        onDoubleClick={() => onClick?.(project.id)}
        cover={
          <ProjectCardPhoto
            project={project}
            photoEditable={photoEditable}
            onUpdateMainPhoto={onUpdateMainPhoto}
          />
        }
      >
        {!!actionItems?.length && (
          <div
            className="absolute top-2 right-2 z-10"
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <Dropdown trigger={["click"]} menu={{ items: actionItems }}>
              <Button
                type={"text"}
                size={"small"}
                icon={<MoreOutlined />}
                aria-label={t("tableActions.rowMenu")}
                className="bg-white/80 shadow-sm hover:bg-white"
              />
            </Dropdown>
          </div>
        )}
        <Flex vertical gap={"small"}>
          <Flex justify="space-between" align="flex-start">
            <div className="flex-1 overflow-hidden">
              <RecordRef
                uid={project?.uid}
                type={typeDisplay}
                city={project?.city}
                state={project?.state}
                customerName={customerName}
                className="mb-1"
              />
              <Title
                ellipsis={{ rows: 1, tooltip: project?.lead_name }}
                level={5}
                className="m-0"
              >
                {project?.lead_name || empty}
              </Title>
            </div>

            {statusEditable ? (
              <ProjectStatusSelector
                status={project?.project_status}
                statuses={statuses}
                canClose={canClose}
                onChange={onStatusChangeHandler}
              />
            ) : (
              <ProjectStatusTag status={project?.project_status} />
            )}
          </Flex>

          {showProgress && (
            <div>
              <Flex justify="space-between" className="mb-1">
                <Text type={"secondary"} className="text-xs ">
                  Progress
                </Text>
                <Text className="text-xs font-medium">{progress}</Text>
              </Flex>
              <Progress
                strokeColor={getProgressColor(progressValue)}
                percent={normalizePercent(project?.progress)}
                showInfo={false}
                size="small"
              />
            </div>
          )}

          <Flex justify="space-between" align="center">
            <Flex gap={8} align="center">
              <Avatar.Group>
                <Avatar
                  src={mainManager?.avatar}
                  size="small"
                  icon={<UserOutlined />}
                />
                {hasOtherManagers && (
                  <Popover
                    content={otherManagersContent}
                    title="Other managers"
                    trigger="hover"
                    placement="bottom"
                  >
                    <Avatar
                      size="small"
                      className="cursor-pointer bg-gray-200 text-gray-600"
                    >
                      <span className="text-xs">+{otherManagers.length}</span>
                    </Avatar>
                  </Popover>
                )}
              </Avatar.Group>
              <div>
                <Text type={"secondary"} className="text-xs block">
                  PM
                </Text>
                <Text className="text-xs font-medium">
                  {getEntityName(mainManager)}
                </Text>
              </div>
            </Flex>
            {showBudget && (
              <div className="text-right">
                <Text type={"secondary"} className="text-sm block">
                  {t("tabbedForm.fields.salesPrice")}
                </Text>
                <Text
                  ellipsis={{ tooltip: formatedPrice }}
                  className="text-base font-semibold"
                >
                  {formatedPrice}
                </Text>
              </div>
            )}
          </Flex>

          <Text
            type={"secondary"}
            className="text-xs"
            ellipsis={{ tooltip: createdLine }}
          >
            {createdLine}
          </Text>
        </Flex>
      </Card>
    )
  }
)

ProjectGridCard.displayName = "ProjectGridCard"
